// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Category } from "@insite/client-framework/Services/CategoryService";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getCollectionPageActiveState from "@monsma/App/Widgets/SearchEngineOptimization/ActiveState/CollectionPage";
import { SeoAppProps, SeoHandlerProps } from "@monsma/App/Widgets/SearchEngineOptimization/Type";
import { CollectionPage, ItemList, WithContext } from "@monsma/App/Widgets/SearchEngineOptimization/Type/schema";

/**
 * Build the structured data
 */

const loadSchemaCollectionPage = ({ state, payload, url }: SeoAppProps): WithContext<CollectionPage> => {
    let subCategories: Category[] = [];
    const currentPage = getCurrentPage(state);
    const location = getLocation(state);

    let schemaData: WithContext<CollectionPage> = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        mainEntity: [],
    };

    if (location.pathname !== payload.allProductsPageUrl) {
        subCategories =
            payload.category?.subCategoryIds?.reduce((results, categoryId) => {
                const category = getCategoryState(state, categoryId).value;
                if (category) {
                    results.push(category);
                }
                return results;
            }, [] as typeof subCategories) || [];
    } else {
        subCategories = payload.categories || [];
    }

    subCategories.forEach(item => {
        const listItem: ItemList = {
            "@type": "ItemList",
            name: item.name || item.shortDescription,
            image: item.smallImagePath,
            description: item.htmlContent?.replace(/(<([^>]+)>)/gi, "") || item.shortDescription,
            url: `${url}${item.path}`,
        };

        if (item.subCategoryIds?.length) {
            listItem.itemListElement = [];

            item.subCategoryIds.forEach(subCategoryId => {
                const category = getCategoryState(state, subCategoryId).value;
                if (!category) {
                    return;
                }

                (listItem.itemListElement as ItemList["itemListElement"][])?.push({
                    "@type": "Thing",
                    name: category.name || category.shortDescription,
                    image: category.smallImagePath,
                    description: category.htmlContent?.replace(/(<([^>]+)>)/gi, "") || category.shortDescription,
                    url: `${url}${category.path}`,
                });
            });
        }

        (schemaData.mainEntity as CollectionPage["mainEntity"][])?.push(listItem);
    });

    if (location.pathname !== payload.allProductsPageUrl) {
        schemaData = {
            ...schemaData,
            name: payload.category?.shortDescription,
            description:
                payload.category?.htmlContent?.replace(/(<([^>]+)>)/gi, "") || payload.category?.shortDescription,
            url: `${url}${payload.category?.path}`,
        };
        return schemaData;
    }

    schemaData = {
        ...schemaData,
        name: currentPage.name,
        url: `${url}${getLocation(state).pathname}`,
    };

    return schemaData;
};

export default {
    isActivate: getCollectionPageActiveState,
    buildSchema: loadSchemaCollectionPage,
} as SeoHandlerProps;
